/**
 * @author Sarah Nouh
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2018-07-24 02:33
 * @description Implementation of  companies and organizations inner page.
 * @filename companies-and-organizations.tsx
 */

import { Types } from "@radivision/common";
import { DynamicList as GraphQlDynamicList } from "@radivision/graphql/lib/ts/graphql/dynamic-list";
import { SearchHit } from "@radivision/graphql/lib/ts/graphql/search-hit";
import { PreviewKind } from "@radivision/graphql/lib/ts/graphql/preview-type";
import { List } from "@radivision/graphql/lib/ts/graphql/list";

import React from "react";
import { fetchQuery, graphql } from "react-relay";
import { FilterContent } from "../../component-configuration/filter-content";
import { ENVIRONMENT } from "../../relay/relay-environment";
import { getCarouselItem } from "../../utilities/general";
import { GridView } from "../lists/grid-view";
import { PanelNavbar } from "../page-framework/panel-navbar";
import { RdvButton } from "../page-framework/rdv-button";
import { SearchFilters } from "../page-framework/search-filters";
import { Loader } from "../page/loader";

const COMPANIES_SEARCH_PROPS: FilterContent[] = [
  {
    filterName: "curated lists",
    checkboxes: [
      { label: "AR/VR", id: "cb1", checked: false },
      { label: "Female Founder", id: "cb2", checked: false },
      { label: "Accelerator Unicorns", id: "cb3", checked: false },
      { label: "Unicorns of North America", id: "cb4", checked: false },
      { label: "Unicorns of Europe", id: "cb5", checked: false },
      { label: "Unicorns of China", id: "cb6", checked: false },
      { label: "Unicorns of India", id: "cb7", checked: false },
    ],
    checkedCount: 0,
  },
  {
    filterName: "Industry",
    checkboxes: [
      { label: "Advertising", id: "cb8", checked: false },
      { label: "AI", id: "cb9", checked: false },
      { label: "Biotechnology", id: "cb10", checked: false },
      { label: "Cannabis", id: "cb11", checked: false },
      { label: "Construction", id: "cb12", checked: false },
      { label: "E-Commerce", id: "cb13", checked: false },
      { label: "Education", id: "cb14", checked: false },
      { label: "Electronics", id: "cb15", checked: false },
      { label: "Energy", id: "cb16", checked: false },
      { label: "FinTech", id: "cb17", checked: false },
      { label: "Gaming", id: "cb18", checked: false },
      { label: "Green Technology", id: "cb19", checked: false },
      { label: "Grocery", id: "cb20", checked: false },
      { label: "Healthcare", id: "cb21", checked: false },
      { label: "IoT", id: "cb22", checked: false },
      { label: "Real Estate", id: "cb23", checked: false },
      { label: "Retail", id: "cb24", checked: false },
      { label: "Robotics", id: "cb25", checked: false },
      { label: "SaaS", id: "cb26", checked: false },
      { label: "Security", id: "cb27", checked: false },
      { label: "Services", id: "cb28", checked: false },
      { label: "Social Media", id: "cb29", checked: false },
      { label: "Transportation", id: "cb30", checked: false },
      { label: "VR", id: "cb31", checked: false },
      { label: "Software", id: "cb32", checked: false },
    ],
    checkedCount: 0,
  },
  {
    filterName: "STAGE",
    checkboxes: [
      { label: "Grants", id: "cb33", checked: false },
      { label: "Angel", id: "cb34", checked: false },
      { label: "Seed Funds", id: "cb35", checked: false },
      { label: "Venture Capital", id: "cb36", checked: false },
      { label: "Public Markets", id: "cb37", checked: false },
    ],
    checkedCount: 0,
  },
  {
    filterName: "fundraising",
    checkboxes: [
      { label: "Actively Fundraising", id: "cb38", checked: false },
      { label: "Not Fundraising", id: "cb39", checked: false },
    ],
    checkedCount: 0,
  },
];

const companiesTags: FilterContent[] = [
  {
    filterName: "Tags",
    checkboxes: [],
    checkedCount: 0,
  },
];

const PEOPLE_SEARCH_PROPS: FilterContent[] = [
  {
    filterName: "curated lists",
    checkboxes: [
      { label: "Serial Founders", id: "pb1", checked: false },
      { label: "Top Women in VC", id: "pb2", checked: false },
      { label: "Founder Turned VC", id: "pb3", checked: false },
      { label: "VCs China", id: "pb4", checked: false },
      { label: "Sand Hill Road", id: "pb5", checked: false },
    ],
    checkedCount: 0,
  },
  {
    filterName: "Industry",
    checkboxes: [
      { label: "Advertising", id: "cb8", checked: false },
      { label: "AI", id: "cb9", checked: false },
      { label: "Biotechnology", id: "cb10", checked: false },
      { label: "Cannabis", id: "cb11", checked: false },
      { label: "Construction", id: "cb12", checked: false },
      { label: "E-Commerce", id: "cb13", checked: false },
      { label: "Education", id: "cb14", checked: false },
      { label: "Electronics", id: "cb15", checked: false },
      { label: "Energy", id: "cb16", checked: false },
      { label: "FinTech", id: "cb17", checked: false },
      { label: "Gaming", id: "cb18", checked: false },
      { label: "Green Technology", id: "cb19", checked: false },
      { label: "Grocery", id: "cb20", checked: false },
      { label: "Healthcare", id: "cb21", checked: false },
      { label: "IoT", id: "cb22", checked: false },
      { label: "Real Estate", id: "cb23", checked: false },
      { label: "Retail", id: "cb24", checked: false },
      { label: "Robotics", id: "cb25", checked: false },
      { label: "SaaS", id: "cb26", checked: false },
      { label: "Security", id: "cb27", checked: false },
      { label: "Services", id: "cb28", checked: false },
      { label: "Social Media", id: "cb29", checked: false },
      { label: "Transportation", id: "cb30", checked: false },
      { label: "VR", id: "cb31", checked: false },
      { label: "Software", id: "cb32", checked: false },
    ],
    checkedCount: 0,
  },
  {
    filterName: "AFFILIATION",
    checkboxes: [
      { label: "Angel", id: "pb35", checked: false },
      { label: "Entrepreneur", id: "pb36", checked: false },
      { label: "Fan", id: "cb8", checked: false },
      { label: "Investment Advisor", id: "pb37", checked: false },
      { label: "Investor", id: "pb38", checked: false },
      { label: "Serial Founder", id: "pb39", checked: false },
      { label: "Midas List", id: "pb40", checked: false },
    ],
    checkedCount: 0,
  },
];

const RESOURCES_SEARCH_PROPS: FilterContent[] = [
  {
    filterName: "curated lists",
    checkboxes: [
      { label: "ai", id: "cb1", checked: false },
      { label: "Agriculture", id: "cb2", checked: false },
      { label: "auto tech", id: "cb3", checked: false },
    ],
    checkedCount: 0,
  },
  {
    filterName: "Industry",
    checkboxes: [
      { label: "women founders", id: "cb4", checked: false },
      { label: "navigate", id: "cb7", checked: false },
      { label: "serial founders", id: "cb10", checked: false },
    ],
    checkedCount: 0,
  },
  {
    filterName: "stage",
    checkboxes: [
      { label: "ecommere & retail", id: "cb5", checked: false },
      { label: "seed funded", id: "cb8", checked: false },
      { label: "serial founders", id: "cb11", checked: false },
    ],
    checkedCount: 0,
  },
  {
    filterName: "fundraising",
    checkboxes: [
      { label: "aaai", id: "cb6", checked: false },
      { label: "Agggriculture", id: "cb9", checked: false },
      { label: "autooo tech", id: "cb12", checked: false },
    ],
    checkedCount: 0,
  },
];

/**
 * The props of the CompaniesAndOrganizations page.
 *
 * @interface
 */

interface CompaniesAndOrganizationsProps {
  /**
   * The list of components in the page.
   *
   * @type {GraphQlDynamicList}
   */

  list: GraphQlDynamicList;
}

/**
 * The Filter Tag object
 *
 * @interface
 */
interface FilterItem {
  /**
   * the filter tag name
   *
   * @type {string}
   * @memberof FilterItem
   */
  filter: string;

  /**
   * the array of items have this tag
   *
   * @type {string[]}
   * @memberof FilterItem
   */
  items: string[];
}

/**
 *
 *
 * @interface CompaniesAndOrganizationsState
 */

interface CompaniesAndOrganizationsState {
  /**
   * The list of components in the page.
   *
   * @type {GraphQlDynamicList}
   */

  buttonText: string;

  /**
   * display the error message if any
   *
   * @type {string}
   */
  errorMsg: string;

  /**
   * fetched filters list
   *
   * @type {FilterItem[]}
   */
  filtersList: FilterItem[];

  /**
   * the whole list data
   *
   * @type {FixedList | DynamicList}
   */
  list: any;

  /**
   * list of companies or people
   */
  listItems: any;

  /**
   * the filtered items
   */
  filteredItems: any;

  /**
   * A boolean to enable progress bar on submit
   *
   * @type {boolean}
   * @memberof NominatePanelState
   */

  loading: boolean;
}

/**
 * A React component that renders the 'companies and organizations' inner page.
 *
 * @export
 * @class CompaniesAndOrganizations
 * @extends {React.Component<companiesProps>}
 */

export class CompaniesAndOrganizations extends React.Component<
  CompaniesAndOrganizationsProps,
  CompaniesAndOrganizationsState
> {
  /**
   * page type
   */

  pageType: "companies" | "people";

  /**
   * Constructor.
   *
   * @param {CompaniesAndOrganizationsProps} props The props of the component.
   */
  /**
   * people and companies query
   *
   * @memberof CompaniesAndOrganizations
   */

  PEOPLE_AND_COMPANIES = graphql`
    query companiesAndOrganizationsMoreQuery($listId: ID!, $count: Int) {
      list(id: $listId) {
        hasErrors
        list {
          __typename
          ... on FixedList {
            description
            id
            items {
              edges {
                cursor
                listItem {
                  item {
                    __typename
                    ... on Entity {
                      description
                      hasPublicPage
                      id
                      name
                      landingPageUrl
                      previews {
                        count
                        edges {
                          cursor
                          preview {
                            mediaAsset {
                              files {
                                id
                                name
                                path
                              }
                              topLevelMediaType {
                                kind
                                label
                              }
                            }
                            link
                            kind
                          }
                        }
                      }
                    }
                    ... on Person {
                      hasPublicPage
                      id
                      fullName
                      previews {
                        count
                        edges {
                          cursor
                          preview {
                            mediaAsset {
                              files {
                                id
                                name
                                path
                              }
                              topLevelMediaType {
                                kind
                                label
                              }
                            }
                            link
                            kind
                          }
                        }
                      }
                      landingPageUrl
                      responsibility
                    }
                    ... on Story {
                      description
                      label
                      previews {
                        count
                        edges {
                          cursor
                          preview {
                            mediaAsset {
                              files {
                                id
                                name
                                path
                              }
                              topLevelMediaType {
                                kind
                                label
                              }
                            }
                            link
                            kind
                          }
                        }
                      }
                      subTitle
                      title
                    }
                  }
                }
              }
            }
            label
            name
            previews {
              count
              edges {
                cursor
                preview {
                  link
                  kind
                  mediaAsset {
                    id
                    files {
                      name
                      path
                    }
                    topLevelMediaType {
                      kind
                      label
                    }
                  }
                }
              }
            }
            subTitle
            title
          }
          ... on DynamicList {
            description
            id
            label
            name
            items(first: $count) {
              edges {
                cursor
                listItem {
                  item {
                    __typename
                    ... on SearchHit {
                      id
                      kind
                      label
                      linkUrl
                      landingPageUrl
                      name
                      previewUrl
                      publisher
                      responsibility
                      subTitle
                      title
                      tags
                    }
                  }
                }
              }
              filter {
                keys
                filter {
                  key
                  ids
                }
              }
            }
            previews {
              count
              edges {
                cursor
                preview {
                  mediaAsset {
                    files {
                      id
                      name
                      path
                    }
                    topLevelMediaType {
                      kind
                      label
                    }
                  }
                  link
                  kind
                }
              }
            }
            subTitle
            title
          }
        }
      }
    }
  `;
  constructor(props: CompaniesAndOrganizationsProps) {
    super(props);

    // ... bind need
    this.changeButtonText = this.changeButtonText.bind(this);
    this.showMore = this.showMore.bind(this);
    this.selectedFilters = this.selectedFilters.bind(this);
    this.updateFilters = this.updateFilters.bind(this);
    this.includeItemOnFilter = this.includeItemOnFilter.bind(this);

    this.state = {
      buttonText: "",
      errorMsg: "",
      filtersList: [],
      list: null,
      listItems: [],
      filteredItems: [],
      loading: false,
    };
  }

  /**
   *to show more items (people or companies) in the page
   *
   * @memberof CompaniesAndOrganizations
   */

  showMore() {
    const counter = this.state.listItems.length + 12;
    this.loadItems(counter);
    this.setState({
      loading: true,
    });
  }

  /**
   * Called immediately before mounting occurs, and before Component#render.
   *
   * @memberof CompaniesAndOrganizations
   */
  componentDidMount() {
    this.loadItems(0);
  }

  /**
   *used to load the items
   *
   * @param {number} [count]
   * @memberof CompaniesAndOrganizations
   */

  loadItems(count?: number) {
    if (!this.props.list || this.props.list.id === "") {
      return <div className="alert alert-danger about-danger">Error Loading List!</div>;
    }
    const listId: string = this.props.list.id;
    const windowSize = window.innerWidth;

    fetchQuery(ENVIRONMENT, this.PEOPLE_AND_COMPANIES, {
      listId,
      count,
    }).then((queryResults: any) => {
      const items = [];
      for (const edge of queryResults.list.list.items.edges) {
        if (edge !== undefined && edge !== null) {
          if (Types.isObject(edge.listItem.item)) {
            items.push(
              getCarouselItem(
                edge,
                queryResults.list.list,
                0,
                {
                  containerWidthRatio: windowSize > 1280 ? 1.5 : 4 / 4,
                  numberOfItems: windowSize > 800 ? 6 : 3,
                },
                PreviewKind.SQUARE,
              ),
            );
          }
        }
      }

      let filtersList: FilterItem[] = this.state.filtersList;
      for (const tag of queryResults.list.list.items.filter.filter) {
        const prettyTag: string = tag.key.charAt(0).toUpperCase() + tag.key.slice(1).replace(/[-_]/g, " ");
        if (!this.checkFilterExists(prettyTag, filtersList)) {
          companiesTags[0].checkboxes.push({
            label: prettyTag,
            id: `cb${companiesTags[0].checkboxes.length + 1}`,
            checked: false,
          });
          filtersList.push({
            filter: prettyTag,
            items: tag.ids,
          });
        } else {
          filtersList = this.updateFilterItems(prettyTag, filtersList, tag.ids);
        }
      }
      this.setState({
        filtersList,
        list: queryResults.list.list,
        listItems: items,
        loading: false,
      });
      this.updateFilters();
    });
  }

  /**
   * to check if filter already exists
   *
   * @param filter the filter to check upon the state list of filters
   * @returns {boolean}
   */
  checkFilterExists(filter: string, filtersList: FilterItem[]): boolean {
    let founded: boolean = false;
    for (const f of filtersList) {
      if (f.filter === filter) {
        founded = true;
        break;
      }
    }
    return founded;
  }

  /**
   * update existing filter with the new set of items ids
   *
   * @param filter the filter to check upon the state list of filters
   * @returns {FilterItem[]}
   */
  updateFilterItems(filter: string, filtersList: FilterItem[], newItems: string[]): FilterItem[] {
    const newfiltersList: FilterItem[] = filtersList.map(
      (f: FilterItem): FilterItem => {
        let newItem: FilterItem = f;
        if (f.filter === filter) {
          newItem = {
            filter: f.filter,
            items: newItems,
          };
        }
        return newItem;
      },
    );
    return newfiltersList;
  }

  /**
   * get the selected filters as an array
   *
   * @returns {string[]} the array of selected filters
   */

  selectedFilters(): string[] {
    const selectedTags: string[] = [];
    const filters: FilterContent[] = this.pageType === "companies" ? companiesTags : PEOPLE_SEARCH_PROPS;
    for (const list of filters) {
      for (const cb of list.checkboxes) {
        if (cb.checked) {
          selectedTags.push(cb.label /*cb.label.toLowerCase()*/);
        }
      }
    }
    return selectedTags;
  }

  /**
   * Triggers when new selection in filters array, happens in search-filters component
   */

  updateFilters() {
    const selectedTags: string[] = this.selectedFilters();
    const newFilteredItems: [] = selectedTags.length
      ? this.state.listItems.filter((item) => this.includeItemOnFilter(item))
      : this.state.listItems;
    this.setState({ filteredItems: newFilteredItems });
  }

  /**
   * to deciede to include the item after filtering or not
   *
   * @param {SearchHit} item the item to compare againest the list of filters
   * @returns {boolean} flag to include the item or not
   */

  includeItemOnFilter(item: SearchHit): boolean {
    const selectedTags: string[] = this.selectedFilters();
    let match: boolean = false;
    for (const selectedTag of selectedTags) {
      for (const filter of this.state.filtersList) {
        if (filter.filter === selectedTag && filter.items.includes(item.id)) {
          match = true;
          break;
        }
      }
    }
    return match;
    // if (item.tags !== null && item.tags !== undefined) {
    //   for (const tag of item.tags) {
    //     const cleanTag: string = tag
    //       .toLocaleLowerCase()
    //       .trim()
    //       .replace(/[-_]/g, " ")
    //       .replace(/  +/g, " ");
    //     if (
    //       selectedTags.includes(tag.toLowerCase()) ||
    //       selectedTags.includes(cleanTag) ||
    //       selectedTags.includes(
    //         cleanTag
    //           .split(" ")
    //           .reverse()
    //           .join(" ")
    //       )
    //     ) {
    //       match = true;
    //       break;
    //     }
    //     for (const selectedTag of selectedTags) {
    //       const cleanSelectedTag: string = selectedTag
    //         .toLocaleLowerCase()
    //         .trim()
    //         .replace(/[-_]/g, " ")
    //         .replace(/  +/g, " ");
    //       if (
    //         cleanTag.includes(cleanSelectedTag) ||
    //         cleanSelectedTag.includes(cleanTag)
    //       ) {
    //         match = true;
    //         break;
    //       }
    //       let score = 0;
    //       for (const word of cleanSelectedTag.split(" ")) {
    //         if (
    //           cleanTag.includes(word) ||
    //           cleanTag.includes(word.replace(/sion|ion|ean|ies|es|s$/g, ""))
    //         ) {
    //           score += 1;
    //         }
    //       }
    //       if (score > cleanSelectedTag.split(" ").length / 2) {
    //         match = true;
    //         break;
    //       }
    //     }
    //   }
    // }
    // return match;
  }

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */

  render(): React.ReactNode {
    if (!this.state.list) {
      return (
        <div className="w-100" style={{ height: "560px" }}>
          <Loader isActive={true} />
        </div>
      );
    }

    const list: List = this.state.list;

    // Temporary hack to hide tab
    const showTabContent = true;
    let buttonText: string = "show more";
    switch (list.label) {
      case "all people":
        buttonText = `${buttonText} People`;
        this.pageType = "people";
        break;

      case "all companies":
        buttonText = `${buttonText} COMPANIES`;
        this.pageType = "companies";
        break;

      default:
    }

    const noPeopleResults = this.pageType === "people" && !this.state.loading &&
      this.state.filteredItems.length === 0 && this.selectedFilters().length > 0;

    return (
      <div className={`companies-entities-section${this.pageType === "people" ? " people-directory" : ""}`}>
        <div className="gray-header">
          <h3 className="text-uppercase title">{list.title}</h3>
          <h6 className="subtitle">{list.description}</h6>
          <PanelNavbar
            gridContent={[list.name]}
            tabState={["active"]}
            tabText={[list.name]}
            hasSearch={true}
            searchText={list.label}
            id={`ppl-and-com-nav-searchable`}
          />
        </div>
        {showTabContent ? (
          <div className="tab-content" id="myTabContent">
            <div className="tab-pane fade show active" id={list.name} role="tabpanel" aria-labelledby="companies-tab">
              <Loader isActive={this.state.loading} />
              {list.name.toLowerCase().indexOf("compan") >= 0 ? (
                <SearchFilters filters={companiesTags} updateFilters={this.updateFilters} />
              ) : (
                <SearchFilters filters={PEOPLE_SEARCH_PROPS} updateFilters={this.updateFilters} />
              )}
              <GridView gridContent={this.state.filteredItems} />
              {noPeopleResults && (
                <p className="d-sm-none text-center py-3" role="status">
                  No people found for the selected filters.
                </p>
              )}
              <RdvButton
                extraClassName={`outline-light view-more-btn${noPeopleResults ? " d-none d-sm-block" : ""}`}
                text={buttonText}
                onClick={this.showMore}
              />
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  /**
   *
   *
   * @param {string} label
   * @memberof CompaniesAndOrganizations
   */

  changeButtonText(label: string) {
    let buttonText: string = this.state.buttonText;
    buttonText = "show more";
    switch (label) {
      case "person":
        buttonText = `${buttonText} people`;
        this.pageType = "people";
        break;
      case "entity":
        buttonText = `${buttonText} company`;
        this.pageType = "companies";
        break;
      default:
    }
    this.setState({ buttonText });
  }
}
